import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
// hooks
import useAccordingToScroll from 'hooks/common/useAccordingToScroll';
// utils
import { formatToPrice } from 'lib/utils/format/number';
import deviceChecker from 'lib/utils/deviceChecker';
// styles
import { palette, mediaQuery } from 'cds/styles';

/**
 * 리뷰 상단 네비게이션바
 * @param {object} props
 * @param {number} props.reviewCount 리뷰 총 개수
 */
const NavigationBar = ({ reviewCount }) => {
  const router = useRouter();
  const { classId } = router.query;

  const [isApp, setIsApp] = useState(null);
  const [view, setView] = useState('PC');

  const [stickyRef, isSticky] = useAccordingToScroll({ start: 84 });

  /**
   * 카테고리 클릭 함수
   * @param {'video' | 'curriculum' | 'introduce' | 'recommend' | 'refund'} type 카테고리 타입
   */
  const onClickCategory = type => {
    const device = deviceChecker();
    if (!device.app()) {
      window.location.href = `${process.env.NEXT_PUBLIC_DEFAULT_HOST}/class/classDetail/${classId}#${type}`;
      return;
    }
    router.back();
  };

  const onScrollTop = () => {
    document.body.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const device = deviceChecker();
    if (device.app()) {
      setIsApp(true);
    } else {
      setIsApp(false);
    }
  }, []);

  /** 화면 넓이 리사이징에 대한 레이아웃 변경 */
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 1178) {
        setView('PC');
      } else {
        setView('Mobile');
      }
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <>
      <Layout ref={stickyRef} isSticky={isSticky} view={view} isApp={isApp}>
        <Tab onClick={() => onClickCategory('videoContainer')}>
          <span>강의 영상</span>
        </Tab>
        <Tab onClick={onScrollTop} selected>
          <span>리뷰({formatToPrice(reviewCount ? reviewCount : 0)})</span>
        </Tab>
        <Tab onClick={() => onClickCategory('curriculum-wrapper')}>
          <span>커리큘럼</span>
        </Tab>
        <Tab onClick={() => onClickCategory('prdInfoClass')}>
          <span>클래스 소개</span>
        </Tab>
        <Tab onClick={() => onClickCategory('recommendation')}>
          <span>추천</span>
        </Tab>
        <Tab onClick={() => onClickCategory('refundInfo')}>
          <span>환불정책</span>
        </Tab>
      </Layout>
      <BlankArea isApp={isApp} />
    </>
  );
};

const Layout = styled.ul`
  display: ${({ isApp }) => (isApp === null ? 'none' : !isApp ? 'flex' : 'none')};
  align-items: center;
  position: fixed;
  top: 56px;
  left: 0;
  width: 100%;
  max-width: 100%;
  height: 56px;
  border-bottom: 1px solid ${palette.grey010};
  background-color: ${palette.white};
  scroll-behavior: smooth;
  white-space: nowrap;
  overflow-x: auto;
  z-index: 2;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mediaQuery.large} {
    display: flex;
    position: ${({ isSticky }) => (isSticky ? 'fixed' : 'absolute')};
    top: 0px;
    left: ${({ isSticky, view }) => {
      if (isSticky) {
        if (view === 'PC') {
          return 'calc((100% - 1178px) / 2 + 12px)';
        } else {
          return '12px';
        }
      } else {
        return '0px';
      }
    }};
    max-width: ${({ isSticky, view, isDisplay }) => {
      if (isSticky && !isDisplay) {
        if (view === 'PC') {
          return 'calc(1178px - 426px - 24px)';
        } else {
          return 'calc(100% - 426px - 24px)';
        }
      }
    }};
  }
`;

const Tab = styled.li`
  position: relative;
  height: 100%;
  vertical-align: top;
  cursor: pointer;
  box-sizing: border-box;
  white-space: nowrap;
  cursor: pointer;

  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0 14px;
    font-size: 14.336px;

    ${mediaQuery.large} {
      font-size: 16px;
    }
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 2px;
    background-color: ${({ selected }) => (selected ? `${palette.black}` : `rgba(0,0,0,0)`)};
  }
`;

const BlankArea = styled.div`
  display: ${({ isApp }) => (!isApp ? 'block' : 'none')};
  height: 56px;

  ${mediaQuery.large} {
    display: block;
  }
`;

export default NavigationBar;
